/**
 * Fonction pour gérer la suppression d'un nœud dans le diagramme CallFlow
 * Gère les cas spéciaux (greeting, holidays, open-hours) et les routings
 */
export const createHandleDeleteNode = (
  setCallFlowData,
  previousCallFlowDataRef,
  saveCallFlowToDatabase
) => {
  return (nodeId) => {
    // Cas spécial: Nœud greeting
    if (nodeId === 'greeting') {  
      setCallFlowData((prev) => {
        const updatedCallFlowData = {
          ...prev,
          greeting: {
            enabled: false,
            welcomeMessage: { enabled: false },
            emergencyMessage: { enabled: false },
            flashMessage: { enabled: false },
            customMessage: { enabled: false },
            messages: []
          }
        };
        
        // Réinitialiser la référence pour forcer la régénération du diagramme
        previousCallFlowDataRef.current = null;
        
        // Sauvegarder dans la base de données
        saveCallFlowToDatabase(updatedCallFlowData)
        return updatedCallFlowData;
      });
      return;
    }
    
    // Cas spécial: Nœud survey
    if (nodeId === 'survey') {  
      setCallFlowData((prev) => {
        const updatedCallFlowData = {
          ...prev,
          survey: {
            ...prev.survey,
            enabled: false
          }
        };
        
        // Réinitialiser la référence pour forcer la régénération du diagramme
        previousCallFlowDataRef.current = null;
        
        // Sauvegarder dans la base de données
        saveCallFlowToDatabase(updatedCallFlowData)
        return updatedCallFlowData;
      });
      return;
    }
    
    // Cas spécial: Nœud holidays
    if (nodeId === 'holidays') {
      setCallFlowData((prev) => {
        const updatedCallFlowData = {
          ...prev,
          deskOpen: {
            ...prev.deskOpen,
            holidays: {
              enabled: false,
              weekendDays: [],
              daysOff: [],
              audio: {},
              holidaysAction: { type: 'disconnect' }
            }
          }
        };
        
        // Réinitialiser la référence pour forcer la régénération du diagramme
        previousCallFlowDataRef.current = null;
        saveCallFlowToDatabase(updatedCallFlowData)
        return updatedCallFlowData;
      });
      return;
    }
    
    // Cas spécial: Nœud open-hours
    if (nodeId === 'open-hours') {
      setCallFlowData((prev) => {
        const updatedCallFlowData = {
          ...prev,
          deskOpen: {
            ...prev.deskOpen,
            openHours: {},
            audio: {}
          }
        };
        
        // Réinitialiser la référence pour forcer la régénération du diagramme
        previousCallFlowDataRef.current = null;
        saveCallFlowToDatabase(updatedCallFlowData)
        return updatedCallFlowData;
      });
      return;
    }
    
    // Cas spécial: Nœud workflow
    if (nodeId === 'workflow' || nodeId.startsWith('workflow-')) {
      // Extraire l'index du workflow
      const workflowMatch = nodeId.match(/^workflow-?(\d*)$/);
      const workflowIndex = workflowMatch && workflowMatch[1] !== '' ? parseInt(workflowMatch[1]) : 0;
      
      setCallFlowData((prev) => {
        const updatedWorkflow = [...(prev.workflow || [])];
        
        // Supprimer le workflow à l'index spécifié
        if (updatedWorkflow[workflowIndex]) {
          updatedWorkflow.splice(workflowIndex, 1);
        }
        
        const updatedCallFlowData = {
          ...prev,
          workflow: updatedWorkflow
        };
        
        // Réinitialiser la référence pour forcer la régénération du diagramme
        previousCallFlowDataRef.current = null;
        
        
        // Sauvegarder dans la base de données
        saveCallFlowToDatabase(updatedCallFlowData)
          .then(() => {
          })
          .catch((error) => {
            console.error('❌ Error saving workflow deletion:', error);
          });
        
        return updatedCallFlowData;
      });
      return;
    }
    
    // Cas 1: Nœud routing (commence par 'routing-')
    if (nodeId.startsWith('routing-')) {
      const routingKey = nodeId.replace('routing-', '');
      
      // Protéger le routing "0" (routing par défaut)
      if (routingKey === '0') {
        return;
      }
      
      setCallFlowData((prev) => {
        if (!prev || !prev.routing) {
          return prev;
        }
        
        const deletedRouting = prev.routing[routingKey];
        if (!deletedRouting) {
          return prev;
        }
        
        
        const deletedRoutingName = deletedRouting?.routingName || routingKey;
        // Supprimer le routing
        const { [routingKey]: removed, ...remainingRoutings } = prev.routing;
        // Nettoyer toutes les références à ce routing dans les autres routings
        Object.keys(remainingRoutings).forEach(rKey => {
          const routing = remainingRoutings[rKey];
          
          // Nettoyer les options IVR
          if ((routing.type === 'ivr' || routing.type === 'IVR') && routing.options) {
            const updatedOptions = {};
            let optionsChanged = false;
            
            
            Object.entries(routing.options).forEach(([dtmf, option]) => {
              
              // Si l'option pointe vers le routing supprimé, ne pas la garder
              if (option.type === 'routing' && 
                  (option.routingName === routingKey || option.routingName === deletedRoutingName)) {
                optionsChanged = true;
              } else if (deletedRouting?.type === 'queue' && option.type === 'queue' && 
                         option.queueId === deletedRouting.queueId) {
                optionsChanged = true;
              } else if (deletedRouting?.type === 'transfer') {
                // Vérifier les différents formats de transfer
                
                if (option.type === 'transfer' && 
                    option.targetType === deletedRouting.targetType &&
                    option.targetId === deletedRouting.targetId) {
                  optionsChanged = true;
                } else if (option.type === 'transferUser' && 
                           deletedRouting.targetType === 'user' && 
                           option.targetId === deletedRouting.targetId) {
                  optionsChanged = true;
                } else if (option.type === 'transferPhone' && 
                           deletedRouting.targetType === 'phone' && 
                           option.targetId === deletedRouting.targetId) {
                  optionsChanged = true;
                } else {
                  updatedOptions[dtmf] = option;
                }
              } else {
                updatedOptions[dtmf] = option;
              }
            });
            
            if (optionsChanged) {
              remainingRoutings[rKey] = {
                ...routing,
                options: updatedOptions
              };
            }
          }
          
          // Nettoyer le next des Prompts
          if (routing.type === 'prompt' && routing.next) {
            if (routing.next.type === 'routing' && 
                (routing.next.routingName === routingKey || routing.next.routingName === deletedRoutingName)) {
              const { next, ...restRouting } = routing;
              remainingRoutings[rKey] = restRouting;
            }
          }
          
          // Nettoyer onMatched/onUnMatched des routings conditionnels
          if (routing.type === 'check_variable' || routing.type === 'time' || routing.type === 'check_days' || routing.type === 'regexp') {
            let updated = false;
            const newRouting = { ...routing };
            
            if (routing.onMatched?.type === 'routing' && 
                (routing.onMatched.routingName === routingKey || routing.onMatched.routingName === deletedRoutingName)) {
              delete newRouting.onMatched;
              updated = true;
            }
            
            if (routing.onUnMatched?.type === 'routing' && 
                (routing.onUnMatched.routingName === routingKey || routing.onUnMatched.routingName === deletedRoutingName)) {
              delete newRouting.onUnMatched;
              updated = true;
            }
            
            if (updated) {
              remainingRoutings[rKey] = newRouting;
            }
          }
        });
      
        const updatedCallFlowData = {
          ...prev,
          routing: remainingRoutings,
        };
        
        // Réinitialiser la référence pour forcer la régénération du diagramme
        previousCallFlowDataRef.current = null;
        
        // Sauvegarder dans la base de données
        saveCallFlowToDatabase(updatedCallFlowData)
        return updatedCallFlowData;
      });
      
      return; // Important: sortir après traitement
    }
    
    // Cas 2: Nœuds visuels créés depuis les routings (sans être connectés)
    // Trouver le routing correspondant et le supprimer
    
    setCallFlowData((prev) => {
      if (!prev || !prev.routing) {
        return prev;
      }
      let routingKeyToDelete = null;
      let routingToDelete = null;
      
      // Chercher le routing qui correspond à ce nodeId
      Object.entries(prev.routing).forEach(([key, routing]) => {
        // Pour disconnect - on prend simplement le premier routing de type disconnect trouvé
        // car tous les disconnect-node visuels représentent le même concept
        if (nodeId === 'disconnect-node' && routing.type === 'disconnect') {
          if (!routingKeyToDelete) {
            routingKeyToDelete = key;
            routingToDelete = routing;
          }
        }
        
        // Pour queue
        if (nodeId.startsWith('queue-')) {
          const queueId = nodeId.replace('queue-', '');
          if (routing.type === 'queue' && routing.queueId === queueId) {
            routingKeyToDelete = key;
            routingToDelete = routing;
          }
        }
        
        // Pour transfer
        if (nodeId.startsWith('transfer-user-')) {
          const userId = nodeId.replace('transfer-user-', '');
          if (routing.type === 'transfer' && routing.targetType === 'user' && routing.targetId === userId) {
            routingKeyToDelete = key;
            routingToDelete = routing;
          }
        }
        
        if (nodeId.startsWith('transfer-pstn-')) {
          const phoneId = nodeId.replace('transfer-pstn-', '');
          if (routing.type === 'transfer' && routing.targetType === 'phone' && routing.targetId === phoneId) {
            routingKeyToDelete = key;
            routingToDelete = routing;
          }
        }
      });
      
      // Si aucun routing n'est trouvé, c'est peut-être juste une option IVR directe
      // Il faut quand même nettoyer les options IVR qui pointent vers ce nœud
      if (!routingKeyToDelete) {
        
        // Extraire les infos du nodeId pour nettoyer les options IVR
        let targetInfo = null;
        
        if (nodeId.startsWith('transfer-user-')) {
          targetInfo = {
            type: 'transfer',
            targetType: 'user',
            targetId: nodeId.replace('transfer-user-', '')
          };
        } else if (nodeId.startsWith('transfer-pstn-')) {
          targetInfo = {
            type: 'transfer',
            targetType: 'phone',
            targetId: nodeId.replace('transfer-pstn-', '')
          };
        } else if (nodeId.startsWith('queue-')) {
          targetInfo = {
            type: 'queue',
            queueId: nodeId.replace('queue-', '')
          };
        }
        
        if (!targetInfo) {
          return prev;
        }
        
        
        // Nettoyer les options IVR même sans routing
        const updatedRoutings = { ...prev.routing };
        let hasChanges = false;
        
        Object.keys(updatedRoutings).forEach(rKey => {
          const routing = updatedRoutings[rKey];
          
          if ((routing.type === 'ivr' || routing.type === 'IVR') && routing.options) {
            const updatedOptions = {};
            let optionsChanged = false;
            
            
            Object.entries(routing.options).forEach(([dtmf, option]) => {
              let shouldRemove = false;
              
              
              // Vérifier si cette option correspond au nœud à supprimer
              if (targetInfo.type === 'transfer') {
                if (option.type === 'transfer' && 
                    option.targetType === targetInfo.targetType &&
                    option.targetId === targetInfo.targetId) {
                  shouldRemove = true;
                } else if (option.type === 'transferUser' && 
                           targetInfo.targetType === 'user' && 
                           option.targetId === targetInfo.targetId) {
                  shouldRemove = true;
                } else if (option.type === 'transferPhone' && 
                           targetInfo.targetType === 'phone' && 
                           option.targetId === targetInfo.targetId) {
                  shouldRemove = true;
                }
              } else if (targetInfo.type === 'queue' && 
                         option.type === 'queue' && 
                         option.queueId === targetInfo.queueId) {
                shouldRemove = true;
              }
              
              if (shouldRemove) {
                optionsChanged = true;
                hasChanges = true;
              } else {
                updatedOptions[dtmf] = option;
              }
            });
            
            if (optionsChanged) {
              updatedRoutings[rKey] = {
                ...routing,
                options: updatedOptions
              };
            }
          }
        });
        
        if (hasChanges) {
          const updatedCallFlowData = {
            ...prev,
            routing: updatedRoutings
          };
          
          previousCallFlowDataRef.current = null;
          saveCallFlowToDatabase(updatedCallFlowData);
          
          return updatedCallFlowData;
        }
        
        return prev;
      }
      
      // Protéger le routing "0"
      if (routingKeyToDelete === '0') {
        return prev;
      }
      
      // Supprimer le routing
      const { [routingKeyToDelete]: removed, ...remainingRoutings } = prev.routing;
      const deletedRoutingName = routingToDelete?.routingName || routingKeyToDelete;
      
      // Nettoyer toutes les références
      Object.keys(remainingRoutings).forEach(rKey => {
        const routing = remainingRoutings[rKey];
        
        // Nettoyer les options IVR
        if ((routing.type === 'ivr' || routing.type === 'IVR') && routing.options) {
          const updatedOptions = {};
          let optionsChanged = false;
          
          
          Object.entries(routing.options).forEach(([dtmf, option]) => {
            
            if (option.type === 'routing' && 
                (option.routingName === routingKeyToDelete || option.routingName === deletedRoutingName)) {
              optionsChanged = true;
            } else if (routingToDelete?.type === 'queue' && option.type === 'queue' && 
                       option.queueId === routingToDelete.queueId) {
              optionsChanged = true;
            } else if (routingToDelete?.type === 'transfer') {
              // Vérifier les différents formats de transfer
              
              if (option.type === 'transfer' && 
                  option.targetType === routingToDelete.targetType &&
                  option.targetId === routingToDelete.targetId) {
                optionsChanged = true;
              } else if (option.type === 'transferUser' && 
                         routingToDelete.targetType === 'user' && 
                         option.targetId === routingToDelete.targetId) {
                optionsChanged = true;
              } else if (option.type === 'transferPhone' && 
                         routingToDelete.targetType === 'phone' && 
                         option.targetId === routingToDelete.targetId) {
                optionsChanged = true;
              } else {
                updatedOptions[dtmf] = option;
              }
            } else if (routingToDelete?.type === 'disconnect' && option.type === 'disconnect') {
              optionsChanged = true;
            } else {
              updatedOptions[dtmf] = option;
            }
          });
          
          if (optionsChanged) {
            remainingRoutings[rKey] = {
              ...routing,
              options: updatedOptions
            };
          }
        }
        
        // Nettoyer le next des Prompts
        if (routing.type === 'prompt' && routing.next) {
          if (routing.next.type === 'routing' && 
              (routing.next.routingName === routingKeyToDelete || routing.next.routingName === deletedRoutingName)) {
            const { next, ...restRouting } = routing;
            remainingRoutings[rKey] = restRouting;
          } else if (routingToDelete?.type === 'disconnect' && routing.next.type === 'disconnect') {
            const { next, ...restRouting } = routing;
            remainingRoutings[rKey] = restRouting;
          }
        }
        // Nettoyer onMatched/onUnMatched des routings conditionnels
        if (routing.type === 'check_variable' || routing.type === 'time' || routing.type === 'check_days' || routing.type === 'regexp') {
          let updated = false;
          const newRouting = { ...routing };
          
          if (routing.onMatched?.type === 'routing' && 
              (routing.onMatched.routingName === routingKeyToDelete || routing.onMatched.routingName === deletedRoutingName)) {
            delete newRouting.onMatched;
            updated = true;
          } else if (routingToDelete?.type === 'disconnect' && routing.onMatched?.type === 'disconnect') {
            delete newRouting.onMatched;
            updated = true;
          }
          
          if (routing.onUnMatched?.type === 'routing' && 
              (routing.onUnMatched.routingName === routingKeyToDelete || routing.onUnMatched.routingName === deletedRoutingName)) {
            delete newRouting.onUnMatched;
            updated = true;
          } else if (routingToDelete?.type === 'disconnect' && routing.onUnMatched?.type === 'disconnect') {
            delete newRouting.onUnMatched;
            updated = true;
          }
          
          if (updated) {
            remainingRoutings[rKey] = newRouting;
          }
        }
      });
    
      const updatedCallFlowData = {
        ...prev,
        routing: remainingRoutings,
      };
      
      // Réinitialiser la référence pour forcer la régénération du diagramme
      previousCallFlowDataRef.current = null;
      
      // Sauvegarder dans la base de données
      saveCallFlowToDatabase(updatedCallFlowData);
      
      return updatedCallFlowData;
    });
  };
};
